/**
 * CacheService.js - Client-side caching service for API responses
 * 
 * Provides in-memory caching with TTL (time to live) for API responses
 * to improve performance and reduce server load.
 */

class CacheService {
  constructor() {
    this.cache = new Map();
    this.ttl = 5 * 60 * 1000; // 5 minutes default TTL
    
    // Clean up expired entries periodically
    this.cleanupInterval = setInterval(() => this.cleanup(), 60 * 1000);
  }

  /**
   * Store a value in the cache
   * @param {string} key - Cache key
   * @param {*} value - Value to store
   * @param {number} [customTtl=null] - Optional custom TTL in milliseconds
   * @returns {*} - The stored value
   */
  set(key, value, customTtl = null) {
    const expires = Date.now() + (customTtl || this.ttl);
    this.cache.set(key, { value, expires });
    
    // Return the cached value
    return value;
  }

  /**
   * Retrieve a value from the cache
   * @param {string} key - Cache key
   * @returns {*} - Cached value or null if not found or expired
   */
  get(key) {
    const entry = this.cache.get(key);
    
    if (!entry) return null;
    
    // Check if entry has expired
    if (Date.now() > entry.expires) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.value;
  }
  
  /**
   * Check if a key exists in the cache and is not expired
   * @param {string} key - Cache key
   * @returns {boolean} - True if key exists and is not expired
   */
  has(key) {
    return this.get(key) !== null;
  }
  
  /**
   * Remove a key from the cache
   * @param {string} key - Cache key
   */
  remove(key) {
    this.cache.delete(key);
  }
  
  /**
   * Clear the entire cache
   */
  clear() {
    this.cache.clear();
  }
  
  /**
   * Get cache statistics
   * @returns {Object} - Cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys())
    };
  }
  
  /**
   * Clean up expired entries
   * @private
   */
  cleanup() {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expires) {
        this.cache.delete(key);
      }
    }
  }
  
  /**
   * Destroy the cache service (clear interval)
   */
  destroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.clear();
  }
}

// Create singleton instance
const cacheService = new CacheService();
export default cacheService;